import { gql } from "@apollo/client";
import { FaustTemplate } from "@faustwp/core";
import { SEO } from "@components";
import { GetEventsPageDataQuery } from "@graphqlTypes";
import FlexibleContentMap from "@/components/ComponentMaps/FlexibleContentMap";
import { FlexiblePageContentFragment } from "@/fragments/FlexibleContent";
import { EventFragment } from "@/fragments/Event";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { HIGHLIGHT_COLOR_BY_TYPE, HIGHLIGHT_COLORS } from "@/core/constants";
import { useEffect } from "react";
import { getBasicQueryVars, getPageTitle } from "@/core/utils";
import { PageDataFragment } from "@/fragments/PageData";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { buildMusicEvent } from "@/core/schema/musicEvent";
import { toAbsoluteProxiedWpUrl } from "@/utils/wpMediaUrl";

const EventsPage: FaustTemplate<GetEventsPageDataQuery> = ({ pageWrapperData, data, loading }: FaustPageProps<GetEventsPageDataQuery>) => {
  const { siteTitle } = usePageWrapperData();
  const { recentStories } = pageWrapperData;
  const { setHighlightColor } = usePageHighlight();
  const { initialEvents } = data;
  const { title, flexiblePageContent, pageData: { pageCategory, ogTitle, ogDescription, featuredImage } = {} } = data?.rhPage || {};
  const seoTitle = getPageTitle(siteTitle, ogTitle || title);
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const [category] = pageCategory || [];
  const itemListNodes = initialEvents?.nodes ?? [];
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: itemListNodes.map((n: any, i: number) => ({
      "@type": "ListItem",
      position: i + 1,
      item: buildMusicEvent({
        name: n.title ?? "",
        url: `https://rockhall.com${n.uri ?? ""}`,
        startDate: n.eventData?.startDate,
        endDate: n.eventData?.endDate,
        description: n.eventData?.eventDescription || n.pageData?.ogDescription,
        imageUrl: toAbsoluteProxiedWpUrl(n.featuredEventImage?.node?.mediaItemUrl || n.pageData?.featuredImage?.node?.mediaItemUrl),
        ticketLink: n.eventData?.ticketLink,
        performerNames: (n.eventData?.performers ?? []).map((p: any) => p?.performerName),
      }),
    })),
  };

  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLOR_BY_TYPE[category?.slug] || HIGHLIGHT_COLORS.GREEN);
  }, [category]);
  useTrackRouteChange(category?.slug);

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  if (loading) return;

  return (
    <>
      <SEO
        title={seoTitle}
        description={ogDescription || ""}
        image={featuredImage}
        jsonLd={itemListNodes.length > 0 ? itemList : undefined}
      />
      <FlexibleContentMap
        content={flexiblePageContent?.content}
        initialEvents={initialEvents}
        recentStories={recentStories}
      />
    </>
  );
};

EventsPage.variables = getBasicQueryVars;

EventsPage.query = gql`
  ${PageDataFragment}
  ${FlexiblePageContentFragment}
  ${EventFragment}
  query GetEventsPageData($databaseId: ID!, $asPreview: Boolean = false, $language: String = "en") {
    rhPage(id: $databaseId, asPreview: $asPreview, language: $language) {
      title
      ...PageData
      flexiblePageContent {
        ...ContentFragment
      }
    }
    initialEvents: events(
      where: { afterToday: true, language: $language, offsetPagination: { size: 12, offset: 0 } }
    ) {
      pageInfo {
        totalCount
        offsetPagination {
          hasMore
        }
      }
      nodes {
        ...EventFragment
      }
    }
  }
`;

export default EventsPage;
